<script setup lang="ts">
import { useLocalStorage, watchDebounced } from '@vueuse/core'
import { isAxiosError } from 'axios'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import type {
  Event,
  ExecuteIliasExportResponse,
  ExportDestination,
  ExportLayout,
  ExportProfile,
  ExportSettings,
  ExportTarget,
  GetIliasCourseStructureResponse,
  GetIliasExportTargetsResponse,
  IliasExportIssue,
  IliasExportMode,
  IliasExportTargetRow,
  RegistrationStatusFilter,
  ValidateIliasExportCourseRefResponse,
  ValidateIliasExportResponse,
} from '@/types'

import { toAppColor } from '@/composables/useAppColor'
import { useAppToast } from '@/composables/useAppToast'
import { exportService } from '@/services/exportService'
import { defaultExportSettings } from '@/types'

import type { IliasOverrideState } from './export-ilias'

import {
  buildIliasValidationSnapshot,
  buildNormalizedIliasRequest,
  getIliasServerValidationKey,
  iliasRowKey,
  shouldRunServerValidation,
  usesSlotTargets,
} from './export-ilias'

const props = defineProps<{
  event: Event
}>()

defineEmits<{
  (e: 'reload-event'): void
}>()

definePage({
  meta: {
    requiresAuth: true,
  },
})

const { t } = useI18n()
const toast = useAppToast()

const settings = ref<ExportSettings>({ ...defaultExportSettings })
const exportTarget = ref<ExportTarget>('file')
const downloading = ref(false)

const iliasOptions = ref({
  mode: 'main-event' as IliasExportMode,
  onlyPaidRegistrations: false,
  groupNamePattern: '{slotLabel}',
  allowGroupMemberWrites: false,
  includeSubSlotParticipants: false,
  groupContainerRef: null as number | null,
})

const iliasValidation = ref<ValidateIliasExportResponse | null>(null)
const iliasValidationSnapshot = ref<ReturnType<typeof buildIliasValidationSnapshot> | null>(null)
const iliasValidationLoading = ref(false)
const iliasValidationError = ref<string | null>(null)
const iliasExportRunning = ref(false)
const iliasResult = ref<ExecuteIliasExportResponse | null>(null)
const overrideState = ref<IliasOverrideState>({ mainCourseRef: null, slotCourseRefs: {} })
const exportTargets = ref<GetIliasExportTargetsResponse | null>(null)
const exportTargetsLoading = ref(false)
const courseStructure = ref<GetIliasCourseStructureResponse | null>(null)
const courseStructureLoading = ref(false)
const courseStructureError = ref(false)

// Per-row ref-ID input state: key = 'main' | slot ID string
const refInputValues = ref<Record<string, string>>({})
const refValidationState = ref<
  Record<
    string,
    { loading: boolean; result: ValidateIliasExportCourseRefResponse | null; error: boolean }
  >
>({})

let validateRequestId = 0

const feeManagementEnabled = computed(() => props.event.workspace?.enableFeeManagement === true)
const isGroupMode = computed(() => iliasOptions.value.mode === 'main-event-with-groups')
const isSlotTargetMode = computed(() => usesSlotTargets(iliasOptions.value.mode))
const hasSubSlots = computed(() =>
  (exportTargets.value?.rows ?? []).some((row) => (row.depth ?? 0) > 0),
)
const isIliasTarget = computed(() => exportTarget.value === 'ilias')

/**
 * The course whose folders the group container picker lists. A hand-typed course reference wins,
 * because that is what the export will use as its main target too.
 */
const groupContainerCourseRef = computed<number | null>(
  () =>
    overrideState.value.mainCourseRef ??
    iliasValidation.value?.targetCourseRef ??
    exportTargets.value?.rows.find((row) => row.rowType === 'main')?.resolvedCourseRef ??
    null,
)
const isFileTarget = computed(() => exportTarget.value === 'file')
const hasMainIliasTarget = computed(() =>
  ['main-event', 'main-event-with-groups', 'main-event-and-campus-event-slots'].includes(
    iliasOptions.value.mode,
  ),
)

/**
 * The one control the user actually operates. File formats and the ILIAS target are a single
 * choice in the UI, while the services keep taking `exportTarget` and `settings.format`
 * separately. Writing through here leaves the existing watchers in charge of the follow-on
 * resets (layout, profile, ILIAS validation).
 */
const destination = computed<ExportDestination>({
  get: () => (isIliasTarget.value ? 'ilias' : settings.value.format),
  set: (next) => {
    if (next === 'ilias') {
      exportTarget.value = 'ilias'
      return
    }

    exportTarget.value = 'file'
    settings.value.format = next
  },
})

const destinationOptions = computed(() => [
  {
    value: 'xlsx' as const,
    label: t('pages.events.export.target.xlsx'),
    hint: t('pages.events.export.target.xlsxHint'),
    icon: 'i-lucide-table',
  },
  {
    value: 'pdf' as const,
    label: t('pages.events.export.target.pdf'),
    hint: t('pages.events.export.target.pdfHint'),
    icon: 'i-lucide-file-text',
  },
  {
    value: 'json' as const,
    label: t('pages.events.export.target.json'),
    hint: t('pages.events.export.target.jsonHint'),
    icon: 'i-lucide-braces',
  },
  {
    value: 'ilias' as const,
    label: t('pages.events.export.target.ilias'),
    hint: t('pages.events.export.target.iliasHint'),
    icon: 'i-lucide-graduation-cap',
  },
])

const iliasModeOptions = computed(() => [
  {
    label: t('pages.events.export.ilias.mode.mainEvent'),
    shortDescription: t('pages.events.export.ilias.mode.mainEventShort'),
    description: t('pages.events.export.ilias.mode.mainEventDescription'),
    value: 'main-event' as const,
  },
  {
    label: t('pages.events.export.ilias.mode.mainEventWithGroups'),
    shortDescription: t('pages.events.export.ilias.mode.mainEventWithGroupsShort'),
    description: t('pages.events.export.ilias.mode.mainEventWithGroupsDescription'),
    value: 'main-event-with-groups' as const,
  },
  {
    label: t('pages.events.export.ilias.mode.campusEventSlots'),
    shortDescription: t('pages.events.export.ilias.mode.campusEventSlotsShort'),
    description: t('pages.events.export.ilias.mode.campusEventSlotsDescription'),
    value: 'campus-event-slots' as const,
  },
  {
    label: t('pages.events.export.ilias.mode.mainEventAndCampusEventSlots'),
    shortDescription: t('pages.events.export.ilias.mode.mainEventAndCampusEventSlotsShort'),
    description: t('pages.events.export.ilias.mode.mainEventAndCampusEventSlotsDescription'),
    value: 'main-event-and-campus-event-slots' as const,
  },
])

const profileOptions = computed<{ label: string; value: ExportProfile }[]>(() => [
  { label: t('pages.events.export.profile.default'), value: 'default' },
  { label: t('pages.events.export.profile.hoc'), value: 'hoc' },
  { label: t('pages.events.export.profile.spzGeneric'), value: 'spz-generic' },
  { label: t('pages.events.export.profile.spzEnglish'), value: 'spz-english' },
  { label: t('pages.events.export.profile.spzSpanish'), value: 'spz-spanish' },
  { label: t('pages.events.export.profile.spzSpanishHueber'), value: 'spz-spanish-hueber' },
])

// People export the same Excel profile (HoC, one of the SPZ variants...) almost every time, so
// the last choice is remembered and restored on open. Whatever storage holds is checked against
// the known profiles first: a profile removed or renamed later must not leave the select blank.
const rememberedProfile = useLocalStorage<ExportProfile>('signmeup:export-profile', 'default')
const restoreRememberedProfile = () => {
  if (profileOptions.value.some((option) => option.value === rememberedProfile.value)) {
    settings.value.profile = rememberedProfile.value
  }
}
restoreRememberedProfile()

const layoutOptions = computed<{ label: string; value: ExportLayout }[]>(() => [
  { label: t('pages.events.export.layout.allInOne'), value: 'all-in-one' },
  { label: t('pages.events.export.layout.sheetPerSlot'), value: 'sheet-per-slot' },
  { label: t('pages.events.export.layout.filePerSlot'), value: 'file-per-slot' },
])

const statusOptions = computed<{ label: string; value: RegistrationStatusFilter }[]>(() => [
  { label: t('enums.registrationStatus.accepted'), value: 'accepted' },
  { label: t('enums.registrationStatus.offer'), value: 'offer' },
  { label: t('enums.registrationStatus.waitlist'), value: 'waitlist' },
  { label: t('enums.registrationStatus.open'), value: 'open' },
  { label: t('enums.registrationStatus.declined'), value: 'declined' },
])

const selectedStateCount = computed(() => settings.value.states.length)

const availableLayoutOptions = computed(() => {
  if (settings.value.format === 'json') {
    return layoutOptions.value.filter((option) => option.value === 'all-in-one')
  }

  if (settings.value.format === 'pdf') {
    return layoutOptions.value
  }

  if (settings.value.profile === 'default') {
    return layoutOptions.value
  }

  if (settings.value.profile === 'hoc') {
    return layoutOptions.value.filter((option) => option.value === 'all-in-one')
  }

  return layoutOptions.value.filter((option) => option.value !== 'sheet-per-slot')
})

// The layout is remembered per profile, because what is allowed depends on it: HoC is always one
// file, the SPZ profiles have no sheet-per-slot. One value for all of them would be wrong as soon
// as the profile changed. Stored values are checked against what the profile currently allows.
const rememberedLayouts = useLocalStorage<Partial<Record<ExportProfile, ExportLayout>>>(
  'signmeup:export-layouts',
  {},
)
const restoreRememberedLayout = () => {
  const layout = rememberedLayouts.value[settings.value.profile]
  if (layout && availableLayoutOptions.value.some((option) => option.value === layout)) {
    settings.value.layout = layout
  }
}
restoreRememberedLayout()

// The attendance-list settings for PDF are remembered the same way, as one object. A stored value
// from an older or hand-edited state is checked field by field and falls back to the default.
const maxPresenceColumns = 14
interface RememberedPdfSettings {
  layout: ExportLayout
  presenceColumns: number
  includeMatriculationId: boolean
  includeEmail: boolean
}
const rememberedPdf = useLocalStorage<Partial<RememberedPdfSettings>>('signmeup:export-pdf', {})
const restoreRememberedPdf = () => {
  const stored = rememberedPdf.value
  if (stored.layout && layoutOptions.value.some((option) => option.value === stored.layout)) {
    settings.value.layout = stored.layout
  }

  if (
    typeof stored.presenceColumns === 'number' &&
    Number.isInteger(stored.presenceColumns) &&
    stored.presenceColumns >= 0 &&
    stored.presenceColumns <= maxPresenceColumns
  ) {
    settings.value.presenceColumns = stored.presenceColumns
  }

  if (typeof stored.includeMatriculationId === 'boolean') {
    settings.value.includeMatriculationId = stored.includeMatriculationId
  }

  if (typeof stored.includeEmail === 'boolean') {
    settings.value.includeEmail = stored.includeEmail
  }
}

const showExtendedOptions = computed(
  () => settings.value.profile === 'default' && settings.value.format === 'xlsx',
)
const currentDestinationLabel = computed(
  () => destinationOptions.value.find((option) => option.value === destination.value)?.label ?? '',
)
const currentProfileLabel = computed(
  () => profileOptions.value.find((option) => option.value === settings.value.profile)?.label ?? '',
)
const currentLayoutLabel = computed(
  () =>
    availableLayoutOptions.value.find((option) => option.value === settings.value.layout)?.label ??
    '',
)
const currentIliasModeLabel = computed(
  () =>
    iliasModeOptions.value.find((option) => option.value === iliasOptions.value.mode)?.label ?? '',
)

const selectedStatusLabels = computed(() =>
  statusOptions.value
    .filter((option) => settings.value.states.includes(option.value))
    .map((option) => option.label),
)

/** Enabled toggles, spelled out in the summary rail so the rail reflects the whole configuration. */
const enabledOptionLabels = computed(() => {
  const base = 'pages.events.export.options'
  const labels: string[] = []

  if (settings.value.includePlannedChanges) labels.push(t(`${base}.includePlannedChanges`))

  if (settings.value.format === 'pdf') {
    if (settings.value.includeMatriculationId) labels.push(t(`${base}.includeMatriculationId`))
    if (settings.value.includeEmail) labels.push(t(`${base}.includeEmail`))
    if (settings.value.presenceColumns > 0) {
      labels.push(`${t(`${base}.presenceColumns`)}: ${settings.value.presenceColumns}`)
    }
  }

  if (showExtendedOptions.value) {
    if (settings.value.includeKITCardNumber) labels.push(t(`${base}.includeKITCardNumber`))
    if (settings.value.includeKITAccount) labels.push(t(`${base}.includeKITAccount`))
    if (settings.value.includeUploads) labels.push(t(`${base}.includeUploads`))
  }

  return labels
})

/** The one-line recap shown in the sticky action bar for file exports. */
const fileSummaryParts = computed(() => {
  const parts = [currentDestinationLabel.value]

  if (settings.value.format === 'xlsx') parts.push(currentProfileLabel.value)
  if (settings.value.format !== 'json') parts.push(currentLayoutLabel.value)

  parts.push(
    selectedStateCount.value === 0
      ? t('pages.events.export.summary.noStatus')
      : t('pages.events.export.summary.statusCount', selectedStateCount.value),
  )

  return parts.filter(Boolean)
})

const fileExportDisabled = computed(() => downloading.value || selectedStateCount.value === 0)
const iliasAvailabilityMessage = computed(() => {
  if (iliasValidationLoading.value) {
    return t('pages.events.export.ilias.messages.validating')
  }

  if (iliasValidationError.value) {
    return iliasValidationError.value
  }

  if (iliasValidation.value && isIliasExportAvailable.value) {
    return t('pages.events.export.layoutLabels.ready')
  }

  return t('pages.events.export.ilias.messages.unavailable')
})
const iliasUnavailableForModeMessage = computed(() =>
  t('pages.events.export.ilias.messages.unavailableForSelectedMode', {
    mode: currentIliasModeLabel.value,
  }),
)
const iliasAvailabilitySeverity = computed(() => {
  if (iliasValidationLoading.value) {
    return 'contrast'
  }

  if (iliasValidationError.value) {
    return 'danger'
  }

  return isIliasExportAvailable.value ? 'success' : 'warn'
})

const iliasServerRequest = computed(() =>
  buildNormalizedIliasRequest(
    {
      mode: iliasOptions.value.mode,
      onlyPaidRegistrations: iliasOptions.value.onlyPaidRegistrations,
      groupNamePattern: iliasOptions.value.groupNamePattern,
      allowGroupMemberWrites: iliasOptions.value.allowGroupMemberWrites,
      includeSubSlotParticipants: iliasOptions.value.includeSubSlotParticipants,
      groupContainerRef: iliasOptions.value.groupContainerRef,
    },
    feeManagementEnabled.value,
    overrideState.value,
  ),
)

const iliasServerValidationKey = computed(() =>
  getIliasServerValidationKey(iliasServerRequest.value),
)

const visibleTargetRows = computed(() => {
  if (!exportTargets.value) return []
  const mode = iliasOptions.value.mode
  if (mode === 'main-event' || mode === 'main-event-with-groups') {
    return exportTargets.value.rows.filter((r) => r.rowType === 'main')
  }
  if (mode === 'campus-event-slots') {
    return exportTargets.value.rows.filter((r) => r.rowType === 'slot')
  }
  return exportTargets.value.rows
})

const localUnavailableReasons = computed(() => {
  const reasons: string[] = []

  if (isGroupMode.value) {
    if (
      iliasOptions.value.allowGroupMemberWrites &&
      iliasValidation.value &&
      !iliasValidation.value.supportsGroupMemberWrites
    ) {
      reasons.push('group-member-writes-unsupported')
    }
  }

  if (iliasOptions.value.allowGroupMemberWrites && !isGroupMode.value) {
    reasons.push('group-member-writes-requires-group-mode')
  }

  if (iliasOptions.value.includeSubSlotParticipants && !isSlotTargetMode.value) {
    reasons.push('sub-slot-rollup-requires-slot-targets')
  }

  return Array.from(new Set(reasons))
})

const unavailableReasons = computed(() => {
  const reasons = [
    ...(iliasValidation.value?.unavailableReasons ?? []),
    ...localUnavailableReasons.value,
  ]

  return Array.from(new Set(reasons))
})

const isIliasExportAvailable = computed(
  () => iliasValidation.value !== null && unavailableReasons.value.length === 0,
)

watch(
  () => settings.value.profile,
  (profile) => {
    // Only Excel has profiles: leaving it resets the profile to the default (watcher below),
    // and that reset is not a choice to remember.
    if (settings.value.format === 'xlsx') {
      rememberedProfile.value = profile
      restoreRememberedLayout()
    }

    if (!availableLayoutOptions.value.some((option) => option.value === settings.value.layout)) {
      settings.value.layout = 'all-in-one'
    }

    if (!showExtendedOptions.value) {
      settings.value.includeKITCardNumber = false
      settings.value.includeKITAccount = false
      settings.value.includeUploads = false
    }
  },
)

watch(
  () => settings.value.layout,
  (layout) => {
    if (settings.value.format === 'xlsx') {
      rememberedLayouts.value = { ...rememberedLayouts.value, [settings.value.profile]: layout }
    } else if (settings.value.format === 'pdf') {
      rememberedPdf.value = { ...rememberedPdf.value, layout }
    }
  },
)

watch(
  () => [
    settings.value.presenceColumns,
    settings.value.includeMatriculationId,
    settings.value.includeEmail,
  ],
  ([presenceColumns, includeMatriculationId, includeEmail]) => {
    if (settings.value.format === 'pdf') {
      rememberedPdf.value = {
        ...rememberedPdf.value,
        presenceColumns: presenceColumns as number,
        includeMatriculationId: includeMatriculationId as boolean,
        includeEmail: includeEmail as boolean,
      }
    }
  },
)

watch(
  () => settings.value.format,
  (format) => {
    if (format === 'xlsx') {
      // Back from PDF/JSON, which reset the profile: bring the remembered one back.
      restoreRememberedProfile()
      restoreRememberedLayout()
    } else {
      settings.value.profile = 'default'
    }

    if (format === 'json') {
      settings.value.layout = 'all-in-one'
    }

    if (format === 'pdf') {
      restoreRememberedPdf()
      if (!availableLayoutOptions.value.some((option) => option.value === settings.value.layout)) {
        settings.value.layout = 'all-in-one'
      }
    } else {
      settings.value.presenceColumns = 0
    }

    if (format !== 'xlsx') {
      settings.value.includeKITCardNumber = false
      settings.value.includeKITAccount = false
      settings.value.includeUploads = false
    }
  },
)

watch(
  () => feeManagementEnabled.value,
  (enabled) => {
    if (!enabled) {
      iliasOptions.value.onlyPaidRegistrations = false
    }
  },
)

watch(
  () => iliasOptions.value.mode,
  (mode) => {
    if (mode !== 'main-event-with-groups') {
      iliasOptions.value.allowGroupMemberWrites = false
      iliasOptions.value.groupContainerRef = null
    }
    if (!usesSlotTargets(mode)) {
      iliasOptions.value.includeSubSlotParticipants = false
    }
    if (isIliasTarget.value) {
      resetOverrides()
      void fetchExportTargets()
    }
  },
)

watch(
  () => props.event.id,
  () => {
    iliasValidation.value = null
    iliasValidationSnapshot.value = null
    iliasValidationError.value = null
    iliasResult.value = null
    iliasOptions.value.groupContainerRef = null
    courseStructure.value = null
    resetOverrides()

    if (isIliasTarget.value) {
      void ensureIliasValidation()
      void fetchExportTargets()
    }
  },
)

watch(
  () => exportTarget.value,
  (target) => {
    if (target === 'ilias') {
      void ensureIliasValidation()
      void fetchExportTargets()
    }
  },
)

// The picker only makes sense in group mode, and only once the target course is known. Reloading
// on a changed course also drops a selection that belonged to the previous course's tree.
watch(
  () => [isIliasTarget.value && isGroupMode.value, groupContainerCourseRef.value] as const,
  ([active, courseRef], previous) => {
    if (!active || courseRef === null) {
      return
    }

    if (previous && previous[0] && previous[1] === courseRef) {
      return
    }

    if (previous?.[1] != null && previous[1] !== courseRef) {
      iliasOptions.value.groupContainerRef = null
    }

    void fetchCourseStructure()
  },
  { immediate: true },
)

// Show what the export would actually use: the backend falls back to the container stored on the
// event, and the organiser should see that rather than an empty box.
watch(
  () => iliasValidation.value?.groupContainerRef ?? null,
  (effectiveContainerRef) => {
    if (effectiveContainerRef !== null && iliasOptions.value.groupContainerRef === null) {
      iliasOptions.value.groupContainerRef = effectiveContainerRef
    }
  },
)

watchDebounced(
  () => (isIliasTarget.value ? iliasServerValidationKey.value : null),
  () => {
    if (!isIliasTarget.value) {
      return
    }

    if (
      !shouldRunServerValidation(
        exportTarget.value,
        iliasValidationSnapshot.value,
        iliasServerRequest.value,
      )
    ) {
      return
    }

    void validateIliasExport()
  },
  { debounce: 350 },
)

const resetOverrides = () => {
  overrideState.value = { mainCourseRef: null, slotCourseRefs: {} }
  refInputValues.value = {}
  refValidationState.value = {}
}

const fetchExportTargets = async () => {
  exportTargetsLoading.value = true
  exportTargets.value = null
  try {
    exportTargets.value = await exportService.getIliasExportTargets(
      props.event.id,
      iliasOptions.value.mode,
    )
  } catch (error) {
    console.error('Failed to fetch ILIAS export targets:', error)
  } finally {
    exportTargetsLoading.value = false
  }
}

const fetchCourseStructure = async () => {
  courseStructureLoading.value = true
  courseStructureError.value = false
  courseStructure.value = null
  try {
    courseStructure.value = await exportService.getIliasCourseStructure(
      props.event.id,
      overrideState.value.mainCourseRef,
    )
  } catch (error) {
    console.error('Failed to fetch ILIAS course structure:', error)
    courseStructureError.value = true
  } finally {
    courseStructureLoading.value = false
  }
}

const validateCourseRef = async (row: IliasExportTargetRow) => {
  const key = iliasRowKey(row)
  const rawValue = refInputValues.value[key] ?? ''
  const refId = parseInt(rawValue, 10)

  // Clear override when input is empty or invalid
  if (!rawValue || isNaN(refId) || refId <= 0) {
    if (row.rowType === 'main') {
      overrideState.value.mainCourseRef = null
    } else if (row.slotId != null) {
      delete overrideState.value.slotCourseRefs[row.slotId]
    }
    refValidationState.value[key] = { loading: false, result: null, error: false }
    return
  }

  refValidationState.value[key] = { loading: true, result: null, error: false }
  try {
    const result = await exportService.validateIliasExportCourseRef(props.event.id, refId)
    refValidationState.value[key] = { loading: false, result, error: false }
    if (result.valid) {
      if (row.rowType === 'main') {
        overrideState.value.mainCourseRef = refId
      } else if (row.slotId != null) {
        overrideState.value.slotCourseRefs[row.slotId] = refId
      }
    } else {
      if (row.rowType === 'main') {
        overrideState.value.mainCourseRef = null
      } else if (row.slotId != null) {
        delete overrideState.value.slotCourseRefs[row.slotId]
      }
    }
  } catch (error) {
    console.error('Course ref validation failed:', error)
    refValidationState.value[key] = { loading: false, result: null, error: true }
    if (row.rowType === 'main') {
      overrideState.value.mainCourseRef = null
    } else if (row.slotId != null) {
      delete overrideState.value.slotCourseRefs[row.slotId]
    }
  }
}

const ensureIliasValidation = async () => {
  if (
    !shouldRunServerValidation(
      exportTarget.value,
      iliasValidationSnapshot.value,
      iliasServerRequest.value,
    )
  ) {
    return
  }

  await validateIliasExport()
}

const validateIliasExport = async () => {
  const requestId = ++validateRequestId
  iliasValidationLoading.value = true
  iliasValidationError.value = null

  try {
    const request = iliasServerRequest.value
    const result = await exportService.validateIliasExport(props.event.id, request)
    if (requestId !== validateRequestId) {
      return
    }

    iliasValidation.value = result
    iliasValidationSnapshot.value = buildIliasValidationSnapshot(request)
  } catch (error) {
    if (requestId !== validateRequestId) {
      return
    }

    console.error('ILIAS validation failed:', error)
    iliasValidation.value = null
    iliasValidationError.value = t('pages.events.export.ilias.messages.validationFailed')
  } finally {
    if (requestId === validateRequestId) {
      iliasValidationLoading.value = false
    }
  }
}

const getUnavailableReasonLabel = (reason: string) => {
  const key = `pages.events.export.ilias.unavailableReasons.${reason}`
  const translated = t(key)
  return translated === key ? t('pages.events.export.ilias.unavailableReasons.unknown') : translated
}

const getIssueCodeLabel = (code: string) => {
  const key = `pages.events.export.ilias.issueCodes.${code}`
  const translated = t(key)
  return translated === key ? code : translated
}

// Issue messages arrive as English text with the variable parts also exposed as
// structured fields, so rebuild them from the issue code and fall back to the
// server text whenever a code has no translation.
const getIssueMessage = (issue: IliasExportIssue) => {
  if (issue.code === 'slot-target-skipped' && !issue.reasonCode) {
    return issue.message
  }

  const variant =
    issue.code === 'course-assignment-failed' && issue.slotId != null
      ? 'course-assignment-failed-slot'
      : issue.code

  const key = `pages.events.export.ilias.issueMessages.${variant}`
  const translated = t(key, {
    client: issue.client ?? '',
    detail: issue.detail ?? '',
    reason: issue.reasonCode ? getUnavailableReasonLabel(issue.reasonCode) : '',
  })

  return translated === key ? issue.message : translated
}

const iliasSummary = computed(() => {
  const result = iliasResult.value
  if (!result) {
    return ''
  }

  const base = 'pages.events.export.ilias.result.summary'
  const main = result.exportedToCourseParticipants
  const slotCourses = result.exportedToSlotCourseParticipants

  const courses =
    main > 0 && slotCourses > 0
      ? t(`${base}.mainAndSlotCourses`, { main, slot: slotCourses })
      : result.mode === 'campus-event-slots' || slotCourses > 0
        ? t(`${base}.slotCourses`, { count: slotCourses })
        : t(`${base}.courses`, { count: main })

  const parts = [
    courses,
    t(`${base}.groups`, { count: result.exportedToGroupParticipants }),
    t(`${base}.failed`, { count: result.failedParticipants }),
    t(`${base}.warnings`, { count: result.warningCount }),
    t(`${base}.errors`, { count: result.errorCount }),
  ]

  return `${t(`pages.events.export.ilias.messages.${result.status}`)}. ${parts.join(', ')}.`
})

const handleDownload = async () => {
  if (settings.value.states.length === 0) {
    toast.add({
      color: 'warning',
      title: t('pages.events.export.errors.noStates'),
      duration: 4000,
    })
    return
  }

  downloading.value = true
  try {
    await exportService.exportSingleEvent(props.event.id, settings.value)
  } catch (error) {
    console.error('Export failed:', error)
    toast.add({
      color: 'error',
      title: t('pages.events.export.errors.downloadFailed'),
      duration: 5000,
    })
  } finally {
    downloading.value = false
  }
}

const handleIliasExport = async () => {
  if (!isIliasExportAvailable.value) {
    toast.add({
      color: 'warning',
      title: t('pages.events.export.ilias.messages.unavailable'),
      duration: 5000,
    })
    return
  }

  iliasExportRunning.value = true
  iliasResult.value = null

  try {
    // Exports exactly what was validated. Listing the fields here instead meant a new option
    // (the group container) was validated but never sent, so the export silently used the
    // default target.
    const result = await exportService.executeIliasExport(props.event.id, {
      ...iliasServerRequest.value,
      mode: iliasOptions.value.mode,
    })
    iliasResult.value = result

    const severity =
      result.status === 'succeeded'
        ? 'success'
        : result.status === 'partiallySucceeded'
          ? 'warning'
          : 'error'

    const summaryKey = `pages.events.export.ilias.messages.${result.status}`
    const summary = t(summaryKey)

    toast.add({
      color: severity,
      title: summary,
      description: iliasSummary.value,
      duration: 6000,
    })
  } catch (error: unknown) {
    console.error('ILIAS export failed:', error)

    if (isAxiosError(error) && error.response?.status === 409) {
      const rawReasons = error.response.data?.reasons
      const reasons = Array.isArray(rawReasons)
        ? rawReasons
            .filter((reason): reason is string => typeof reason === 'string' && reason.length > 0)
            .map((reason) => getUnavailableReasonLabel(reason))
            .join('\n')
        : ''

      toast.add({
        color: 'warning',
        title: iliasUnavailableForModeMessage.value,
        description: reasons || undefined,
        duration: 8000,
      })
    } else {
      toast.add({
        color: 'error',
        title: t('pages.events.export.ilias.messages.executeFailed'),
        duration: 6000,
      })
    }
  } finally {
    iliasExportRunning.value = false
  }
}
</script>

<template>
  <div class="mx-auto flex w-full max-w-7xl flex-col gap-6">
    <header class="flex items-start gap-3">
      <span
        class="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-blue-200 bg-blue-100 text-blue-700 dark:border-blue-500/40 dark:bg-blue-500/20 dark:text-blue-300"
      >
        <UIcon name="i-lucide-file-up" class="text-xl" />
      </span>
      <div>
        <h2 class="text-xl font-semibold text-neutral-900 dark:text-white">
          {{ t('pages.events.export.title') }}
        </h2>
        <p class="mt-1 text-sm text-muted">
          {{
            isFileTarget
              ? t('pages.events.export.subtitle')
              : t('pages.events.export.ilias.subtitle')
          }}
        </p>
      </div>
    </header>

    <!-- Configuration on the left, the summary rail on the right once the viewport can hold both. -->
    <div class="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_21rem]">
      <div class="flex min-w-0 flex-col gap-6">
        <ExportTargetPicker
          v-model="destination"
          :options="destinationOptions"
          :legend="t('pages.events.export.target.label')"
        />

        <template v-if="isFileTarget">
          <ExportFormatSettings
            v-model="settings"
            :profile-options="profileOptions"
            :layout-options="availableLayoutOptions"
          />
          <ExportStatusFilter v-model="settings.states" :options="statusOptions" />
          <ExportFileOptions v-model="settings" :show-extended-options="showExtendedOptions" />
        </template>

        <template v-else>
          <IliasExportSettings
            v-model:mode="iliasOptions.mode"
            v-model:only-paid-registrations="iliasOptions.onlyPaidRegistrations"
            v-model:group-name-pattern="iliasOptions.groupNamePattern"
            v-model:allow-group-member-writes="iliasOptions.allowGroupMemberWrites"
            v-model:include-sub-slot-participants="iliasOptions.includeSubSlotParticipants"
            v-model:group-container-ref="iliasOptions.groupContainerRef"
            :options="iliasModeOptions"
            :fee-management-enabled="feeManagementEnabled"
            :is-group-mode="isGroupMode"
            :is-slot-target-mode="isSlotTargetMode"
            :has-sub-slots="hasSubSlots"
            :supports-group-member-writes="iliasValidation?.supportsGroupMemberWrites ?? null"
            :group-container-nodes="courseStructure?.nodes ?? []"
            :group-container-course-ref="groupContainerCourseRef"
            :group-container-loading="courseStructureLoading"
            :group-container-error="courseStructureError"
            :group-container-truncated="courseStructure?.truncated ?? false"
            :group-name-preview="iliasValidation?.groupNamePreview ?? []"
            :group-name-preview-loading="iliasValidationLoading"
          />

          <IliasExportTargets
            v-model="refInputValues"
            :rows="visibleTargetRows"
            :loading="exportTargetsLoading"
            :validation-state="refValidationState"
            @validate="validateCourseRef"
          />

          <AppAlert v-if="iliasValidationError" color="error" :closable="false">
            {{ iliasValidationError }}
          </AppAlert>

          <IliasExportPreview
            v-if="iliasValidation"
            :validation="iliasValidation"
            :has-main-target="hasMainIliasTarget"
            :unavailable-reasons="unavailableReasons"
            :unavailable-title="iliasUnavailableForModeMessage"
            :reason-label="getUnavailableReasonLabel"
          />

          <IliasExportResult
            v-if="iliasResult"
            :result="iliasResult"
            :issue-code-label="getIssueCodeLabel"
            :issue-message="getIssueMessage"
            :summary="iliasSummary"
          />
        </template>
      </div>

      <ExportSummaryPanel :title="t('pages.events.export.summary.title')">
        <template #badge>
          <UBadge
            v-if="!isFileTarget"
            :color="toAppColor(iliasAvailabilitySeverity)"
            variant="subtle"
            class="max-w-full whitespace-normal"
            :label="iliasAvailabilityMessage"
          />
          <UBadge
            v-else-if="selectedStateCount === 0"
            color="warning"
            variant="subtle"
            :label="t('pages.events.export.summary.noStatus')"
          />
        </template>

        <template #details>
          <dl v-if="isFileTarget" class="flex flex-col">
            <ExportSummaryRow
              :label="t('pages.events.export.target.label')"
              :value="currentDestinationLabel"
            />
            <ExportSummaryRow
              v-if="settings.format === 'xlsx'"
              :label="t('pages.events.export.profile.label')"
              :value="currentProfileLabel"
            />
            <ExportSummaryRow
              v-if="settings.format !== 'json'"
              :label="t('pages.events.export.layout.label')"
              :value="currentLayoutLabel"
            />
            <ExportSummaryRow :label="t('pages.events.export.states.label')">
              <div v-if="selectedStatusLabels.length" class="flex flex-wrap gap-1">
                <UBadge
                  v-for="label in selectedStatusLabels"
                  :key="label"
                  color="neutral"
                  variant="subtle"
                  size="sm"
                  :label="label"
                />
              </div>
              <span v-else class="text-sm text-warning">
                {{ t('pages.events.export.errors.noStates') }}
              </span>
            </ExportSummaryRow>
            <ExportSummaryRow :label="t('pages.events.export.options.label')">
              <ul
                v-if="enabledOptionLabels.length"
                class="flex flex-col gap-1 text-sm text-neutral-900 dark:text-white"
              >
                <li
                  v-for="label in enabledOptionLabels"
                  :key="label"
                  class="flex items-start gap-2"
                >
                  <UIcon name="i-lucide-check" class="mt-0.5 shrink-0 text-success" />
                  <span>{{ label }}</span>
                </li>
              </ul>
              <span v-else class="text-sm text-muted">
                {{ t('pages.events.export.summary.none') }}
              </span>
            </ExportSummaryRow>
          </dl>

          <dl v-else class="flex flex-col">
            <ExportSummaryRow
              :label="t('pages.events.export.ilias.mode.label')"
              :value="currentIliasModeLabel"
            />
            <ExportSummaryRow
              v-if="iliasValidation"
              :label="t('pages.events.export.ilias.labels.targetClient')"
              :value="iliasValidation.targetClient ?? '—'"
            />
            <ExportSummaryRow
              v-if="unavailableReasons.length"
              :label="t('pages.events.export.layoutLabels.availability')"
            >
              <ul class="flex flex-col gap-1 text-sm text-warning">
                <li v-for="reason in unavailableReasons" :key="reason">
                  {{ getUnavailableReasonLabel(reason) }}
                </li>
              </ul>
            </ExportSummaryRow>
          </dl>
        </template>

        <template #compact>
          <template v-if="isFileTarget">
            <div class="truncate text-sm font-medium text-neutral-900 dark:text-white">
              {{ fileSummaryParts[0] }}
            </div>
            <div class="truncate text-sm text-muted">
              {{ fileSummaryParts.slice(1).join(' · ') }}
            </div>
          </template>
          <template v-else>
            <div class="truncate text-sm font-medium text-neutral-900 dark:text-white">
              {{ currentIliasModeLabel }}
            </div>
            <UBadge
              class="mt-1"
              :color="toAppColor(iliasAvailabilitySeverity)"
              :label="iliasAvailabilityMessage"
            />
          </template>
        </template>

        <template #action>
          <UButton
            v-if="isFileTarget"
            :label="
              downloading
                ? t('pages.events.export.actions.downloading')
                : t('pages.events.export.actions.download')
            "
            icon="i-lucide-download"
            size="lg"
            class="w-full justify-center max-xl:sm:w-auto"
            :loading="downloading"
            :disabled="fileExportDisabled"
            @click="handleDownload"
          />
          <UButton
            v-else
            :label="
              iliasExportRunning
                ? t('pages.events.export.ilias.actions.running')
                : t('pages.events.export.ilias.actions.start')
            "
            icon="i-lucide-send"
            size="lg"
            class="w-full justify-center max-xl:sm:w-auto"
            :loading="iliasExportRunning"
            :disabled="iliasExportRunning || !isIliasExportAvailable"
            @click="handleIliasExport"
          />
        </template>
      </ExportSummaryPanel>
    </div>
  </div>
</template>
